import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { GET_USER_ACCOUNTS } from '../../graphql/queries';
import { CREATE_WP_CONNECTION } from '../../graphql/mutations';
import { Globe, ShieldCheck, ArrowRight, Loader2, AlertTriangle } from 'lucide-react';

interface AccountOption {
  id: string;
  name: string;
  isActive?: boolean;
}

/** Same-host check: the plugin's callback must live on the same site we're connecting. */
function sameHost(a: string, b: string): boolean {
  try {
    return new URL(a).host.toLowerCase() === new URL(b).host.toLowerCase();
  } catch {
    return false;
  }
}

/**
 * Landing page for the WordPress plugin's "Connect" button. Reads the site,
 * name, callback, and state passed through from the plugin, has the user pick
 * an account, mints a pairing code, and redirects back to the site's admin with
 * ?smagent_pairing_code=...&state=... The plugin then exchanges the code for a
 * durable token (server-to-server).
 */
export function ConnectWordPress() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const site = params.get('site') || '';
  const siteName = params.get('name') || '';
  const callback = params.get('callback') || '';
  const state = params.get('state') || '';

  const [accountId, setAccountId] = useState<string>('');
  const [error, setError] = useState<string>('');

  // Not logged in → bounce to login, returning here (with query) afterwards.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      sessionStorage.setItem('postLoginRedirect', '/connect' + window.location.search);
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const { data, loading: accountsLoading } = useQuery(GET_USER_ACCOUNTS, {
    skip: !isAuthenticated,
  });
  const accounts: AccountOption[] = useMemo(() => data?.userAccounts || [], [data]);

  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      setAccountId(accounts[0].id);
    }
  }, [accounts, accountId]);

  const [createConnection, { loading: creating }] = useMutation(CREATE_WP_CONNECTION);

  // Guard against being used as an open redirect: the callback must be on the
  // same host as the site we're connecting.
  const paramsValid = Boolean(site && callback && state);
  const callbackTrusted = paramsValid && sameHost(site, callback);

  const handleApprove = async () => {
    setError('');
    if (!accountId) {
      setError('Choose an account to connect.');
      return;
    }
    try {
      const res = await createConnection({
        variables: { accountId, siteUrl: site, siteName: siteName || null },
      });
      const code = res.data?.createWpConnection?.code;
      if (!code) {
        setError('Could not create the connection. Please try again.');
        return;
      }
      const sep = callback.includes('?') ? '&' : '?';
      window.location.href = `${callback}${sep}smagent_pairing_code=${encodeURIComponent(
        code
      )}&state=${encodeURIComponent(state)}`;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
    }
  };

  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cmp-paper text-white">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cmp-paper via-cmp-surface to-cmp-paper p-4">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-8 text-white shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-400">
            <Globe className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">Connect WordPress</h1>
            <p className="text-sm text-white/60">Link this site to PostMaster</p>
          </div>
        </div>

        {!paramsValid ? (
          <div className="flex items-start gap-2 text-amber-300 text-sm">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <p>
              This link is missing connection details. Start the connection from the plugin in your
              WordPress admin.
            </p>
          </div>
        ) : !callbackTrusted ? (
          <div className="flex items-start gap-2 text-amber-300 text-sm">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <p>
              The return address doesn't match the site being connected, so we stopped for safety.
              Retry from your WordPress admin.
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-lg bg-black/20 border border-white/10 px-4 py-3 mb-6">
              <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Site</div>
              <div className="font-medium break-all">{siteName || site}</div>
              <div className="text-sm text-white/50 break-all">{site}</div>
            </div>

            <label className="block text-sm text-white/70 mb-1">Connect to account</label>
            {accountsLoading ? (
              <div className="text-white/50 text-sm py-2">Loading accounts…</div>
            ) : accounts.length === 0 ? (
              <div className="text-amber-300 text-sm mb-4">
                You don't have an account yet.{' '}
                <button className="underline" onClick={() => navigate('/signup')}>
                  Create one
                </button>{' '}
                first, then reconnect.
              </div>
            ) : (
              <select
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full rounded-lg bg-black/30 border border-white/10 px-3 py-2 mb-6 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id} className="bg-cmp-surface">
                    {a.name}
                  </option>
                ))}
              </select>
            )}

            {error && <div className="text-red-400 text-sm mb-4">{error}</div>}

            <button
              onClick={handleApprove}
              disabled={creating || accounts.length === 0}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-medium px-4 py-2.5 transition-colors"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Connecting…
                </>
              ) : (
                <>
                  Approve &amp; connect <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <div className="flex items-center gap-2 text-xs text-white/40 mt-4">
              <ShieldCheck className="w-4 h-4 shrink-0" />
              <span>
                You'll be returned to your WordPress admin to finish. Your content stays private to
                this account.
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ConnectWordPress;
